import { useEffect, useMemo, useState } from "react";
import { Ban, ShieldX, Plus, Trash2, Clock, Save, AlertTriangle, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, KPICard, PageHeader, StatusPill } from "@/components/uiext";
import { InlineEditorShell } from "@/components/InlineEditorShell";
import { useConfirm } from "@/lib/confirm.jsx";
import { toast } from "sonner";

/**
 * Shared Firewall admin page. `scope` is "tenant" or "platform".
 *  - tenant uses /api/firewall/*
 *  - platform uses /api/platform/firewall/* and covers cross-org + global rules
 */
export default function FirewallPage({ scope = "tenant" }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const base = scope === "platform" ? "/platform/firewall" : "/firewall";
  const [bans, setBans] = useState([]);
  const [rules, setRules] = useState([]);
  const [offenders, setOffenders] = useState([]);
  const [allowlist, setAllowlist] = useState([]);
  const [allowForm, setAllowForm] = useState({ cidr: "", label: "" });
  const [allowAdding, setAllowAdding] = useState(false);
  const [includeExpired, setIncludeExpired] = useState(false);
  const [banOpen, setBanOpen] = useState(false);
  const [banForm, setBanForm] = useState({ ip: "", reason: "", preset: "1h", duration_seconds: 0 });
  const [savingRules, setSavingRules] = useState(false);

  const load = () => {
    api
      .get(`${base}/bans${includeExpired ? "?include_expired=true" : ""}`)
      .then((r) => setBans(r.data))
      .catch(() => {});
    api
      .get(`${base}/rules`)
      .then((r) => setRules(r.data.rules || []))
      .catch(() => {});
    api
      .get(`${base}/offenders?window_hours=24&min_bans=2&limit=20`)
      .then((r) => setOffenders(r.data || []))
      .catch(() => {});
    api
      .get(`${base}/allowlist`)
      .then((r) => setAllowlist(r.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, includeExpired]);

  const addBan = async () => {
    try {
      await api.post(`${base}/bans`, banForm);
      toast.success(`Banned ${banForm.ip}`);
      setBanForm({ ip: "", reason: "", preset: "1h", duration_seconds: 0 });
      setBanOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ban failed");
    }
  };

  const unban = async (b) => {
    const _ok = await confirm({ title: `Remove ban for ${b.ip}?`, danger: true, confirmLabel: t("common.delete") || "Delete" });
    if (!_ok) return;
    // Optimistic UI: drop the row immediately so the operator sees their
    // click took effect even if the engine's auto-ban window re-applies
    // the same IP within 2-3 seconds.
    setBans((prev) => prev.filter((x) => x.id !== b.id));
    try {
      await api.delete(`${base}/bans/${b.id}`);
      toast.success(
        t("firewall.unbanSuccess", "Unbanned {{ip}}", { ip: b.ip }) +
          " — " +
          t(
            "firewall.unbanHint",
            "use Trust forever to prevent re-bans",
          )
      );
      load();
    } catch (e) {
      // Roll back optimistic removal on failure and surface the error.
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 404) {
        toast.error(
          t(
            "firewall.unbanNotFound",
            "This ban is platform-scoped — ask a platform admin to clear it.",
          )
        );
      } else {
        toast.error(detail || t("firewall.unbanFailed", "Unban failed"));
      }
      load(); // re-pull from server to restore truth
    }
  };

  // Hotfix Z.8 — "Unban + Trust forever". Atomic on the backend: removes
  // the ban row, signals the engine to drop in-memory state, AND inserts
  // a /32 (or /128 for v6) allowlist entry so the IP can never be
  // re-banned via cumulative / per-event-type rules. Solves the
  // recurring "Zoiper softphone keeps getting banned" complaint.
  const unbanAndTrust = async (b) => {
    const _ok = await confirm({
      title: t("firewall.trustForeverTitle", "Unban + trust forever?"),
      description: t(
        "firewall.trustForeverDesc",
        "Remove this ban AND add {{ip}} to the firewall allowlist so the engine never bans it again. Use this for legitimate endpoints (softphones, office IPs) that occasionally trip the cumulative window.",
        { ip: b.ip },
      ),
      confirmLabel: t("firewall.trustForever", "Unban + Trust forever"),
    });
    if (!_ok) return;
    // Optimistic removal — same rationale as `unban`.
    setBans((prev) => prev.filter((x) => x.id !== b.id));
    try {
      await api.post(`${base}/bans/${b.id}/trust`, {
        label: `Unban + trust by operator on ${new Date().toLocaleString()}`,
      });
      toast.success(
        t("firewall.trustForeverSuccess", "{{ip}} unbanned and added to allowlist", { ip: b.ip })
      );
      load();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error(
          t(
            "firewall.unbanNotFound",
            "This ban is platform-scoped — ask a platform admin to clear it.",
          )
        );
      } else {
        toast.error(
          e?.response?.data?.detail || t("firewall.trustForeverFailed", "Could not allowlist")
        );
      }
      load();
    }
  };

  const addAllowlistEntry = async () => {
    const cidr = (allowForm.cidr || "").trim();
    if (!cidr) {
      toast.error(t("firewall.allowlistIpRequired", "IP or CIDR is required"));
      return;
    }
    setAllowAdding(true);
    try {
      await api.post(`${base}/allowlist`, {
        cidr,
        label: allowForm.label || "",
      });
      toast.success(t("firewall.allowlistAdded", "Allowlisted {{cidr}}", { cidr }));
      setAllowForm({ cidr: "", label: "" });
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("firewall.allowlistAddFailed", "Add failed")
      );
    } finally {
      setAllowAdding(false);
    }
  };

  // Hotfix Z.14 — bulk-delete every active ban row in the current scope.
  // Operator pain: after a brute-force burst there can be dozens of ban
  // rows, clicking the trash icon on every row is tedious.
  const clearAllBans = async () => {
    const n = activeBans.length;
    if (n === 0) return;
    const _ok = await confirm({
      title: t("firewall.clearAllBansConfirmTitle", "Clear all bans?"),
      description: t(
        "firewall.clearAllBansConfirmDesc",
        "This will remove every active ban row ({{n}}) in this scope. The engine in-memory state is also cleared, so previously-banned IPs immediately become unblocked. Audit-logged. Use Allowlist or Trust forever for IPs that must never be re-banned.",
        { n },
      ),
      danger: true,
      confirmLabel: t("firewall.clearAllBans", "Clear all bans"),
    });
    if (!_ok) return;
    // Optimistic UI: empty the table immediately.
    setBans([]);
    try {
      const r = await api.delete(`${base}/bans`);
      toast.success(
        t("firewall.clearAllBansDone", "Cleared {{n}} bans", { n: r.data?.cleared ?? n })
      );
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          t("firewall.clearAllBansFailed", "Failed to clear all bans"),
      );
      load();
    }
  };

  const removeAllowlistEntry = async (row) => {
    const _ok = await confirm({
      title: t("firewall.allowlistRemoveTitle", "Remove from allowlist?"),
      description: t(
        "firewall.allowlistRemoveDesc",
        "After removal, {{cidr}} can be banned again by engine rules.",
        { cidr: row.cidr },
      ),
      danger: true,
      confirmLabel: t("common.delete") || "Delete",
    });
    if (!_ok) return;
    try {
      await api.delete(`${base}/allowlist/${row.id}`);
      toast.success(t("firewall.allowlistRemoved", "Removed {{cidr}}", { cidr: row.cidr }));
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("firewall.allowlistRemoveFailed", "Remove failed")
      );
    }
  };

  // Hotfix Z.3 — Repeat-offender row controls. Both DELETE endpoints are
  // platform-gated on the backend, so we only render the buttons when
  // `scope === "platform"`. Optimistic UI: we filter the offender out of
  // local state immediately so the operator gets instant feedback even
  // if the 5s poll hasn't fired yet.
  const handleDeleteOffender = async (ip) => {
    const _ok = await confirm({
      title: t("firewall.clearOffender", "Clear bans for this IP"),
      description: t("firewall.clearOffenderConfirm", "Clear every ban for {{ip}}?", { ip }),
      danger: true,
      confirmLabel: t("common.delete") || "Delete",
    });
    if (!_ok) return;
    try {
      const r = await api.delete(`${base}/offenders/${encodeURIComponent(ip)}`);
      const count = r?.data?.deleted ?? 0;
      setOffenders((prev) => prev.filter((o) => o.ip !== ip));
      toast.success(
        t("firewall.clearOffenderSuccess", "Cleared {{count}} ban(s) for {{ip}}", { count, ip })
      );
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("firewall.clearOffenderFailed", "Clear failed")
      );
    }
  };

  const handleClearAllOffenders = async () => {
    const _ok = await confirm({
      title: t("firewall.clearAllOffenders", "Clear all"),
      description: t(
        "firewall.clearAllOffendersConfirm",
        "Clear every engine-applied ban in the last 24h? Operator-set bans are not affected."
      ),
      danger: true,
      confirmLabel: t("common.delete") || "Delete",
    });
    if (!_ok) return;
    try {
      const r = await api.delete(`${base}/offenders?window_hours=24`);
      const count = r?.data?.deleted ?? 0;
      setOffenders([]);
      toast.success(
        t("firewall.clearAllOffendersSuccess", "Cleared {{count}} engine-applied ban(s)", { count })
      );
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("firewall.clearOffenderFailed", "Clear failed")
      );
    }
  };

  const setRule = (et, patch) => {
    setRules((prev) =>
      prev.map((r) => (r.event_type === et ? { ...r, ...patch, source: "edited" } : r))
    );
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      const payload = rules.map(({ event_type, window_seconds, max_count, ban_duration_seconds, enabled }) => ({
        event_type,
        window_seconds: Number(window_seconds),
        max_count: Number(max_count),
        ban_duration_seconds: Number(ban_duration_seconds),
        enabled: !!enabled,
      }));
      const r = await api.put(`${base}/rules`, { rules: payload });
      setRules(r.data.rules || []);
      toast.success("Firewall rules saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingRules(false);
    }
  };

  const resetToDefaults = async () => {
    // Push every row back to the *baseline* (engine DEFAULT_RULES). For
    // tenant scope this clears their org-specific overrides; for platform
    // scope this resets the global override layer. The backend keeps
    // org/global rows around but we just stamp them with the engine
    // defaults so the runtime behaviour matches a fresh install.
    const DEFAULTS = {
      auth_fail:       { window_seconds: 60, max_count: 5,  ban_duration_seconds: 600 },
      ip_auth_fail:    { window_seconds: 60, max_count: 5,  ban_duration_seconds: 600 },
      domain_mismatch: { window_seconds: 30, max_count: 5,  ban_duration_seconds: 1800 },
      // Phase 4k tightening — slow scanners every ~30s would never reach
      // 5/60s. 3/60s catches them in ~2 minutes.
      domain_reject:   { window_seconds: 60, max_count: 3,  ban_duration_seconds: 1800 },
      acl_block:       { window_seconds: 60, max_count: 10, ban_duration_seconds: 600 },
      // Phase 4k — pure recon, no honest reason to keep hitting suspended
      // tenants. 2/60s.
      tenant_inactive: { window_seconds: 60, max_count: 2,  ban_duration_seconds: 1800 },
      ip_locked:       { window_seconds: 1,  max_count: 1,  ban_duration_seconds: 14400 },
    };
    setSavingRules(true);
    try {
      const payload = Object.entries(DEFAULTS).map(([k, v]) => ({
        event_type: k,
        ...v,
        enabled: true,
      }));
      const r = await api.put(`${base}/rules`, { rules: payload });
      setRules(r.data.rules || []);
      toast.success(t("firewall.resetSuccess", "Firewall rules reset to defaults"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setSavingRules(false);
    }
  };

  const now = Date.now();
  const activeBans = useMemo(
    () => bans.filter((b) => new Date(b.expires_at).getTime() > now),
    [bans, now]
  );
  const autoBans = useMemo(
    () => activeBans.filter((b) => b.actor_scope === "engine"),
    [activeBans]
  );

  // Phase N+ — 3CX/Vodia-style inline editor takes over the whole page.
  if (banOpen) {
    return (
      <InlineEditorShell
        crumb={t("firewall.title")}
        title={t("firewall.addBan")}
        onCancel={() => setBanOpen(false)}
        onSave={addBan}
        saveLabel={t("firewall.ban")}
        saveDisabled={!banForm.ip?.trim()}
        testidRoot="firewall-ban-editor"
      >
        <div className="space-y-3">
          <div>
            <Label>{t("firewall.cols.ip")}</Label>
            <Input
              value={banForm.ip}
              onChange={(e) => setBanForm({ ...banForm, ip: e.target.value })}
              placeholder="e.g. 203.0.113.42"
              data-testid="firewall-ban-ip"
            />
          </div>
          <div>
            <Label>{t("firewall.cols.reason")}</Label>
            <Input
              value={banForm.reason}
              onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })}
              placeholder="Scanner from DigitalOcean"
              data-testid="firewall-ban-reason"
            />
          </div>
          <div>
            <Label>{t("firewall.cols.banDuration")}</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="firewall-ban-presets">
              {[
                { code: "1h",        label: "1 hour" },
                { code: "1d",        label: "1 day" },
                { code: "7d",        label: "7 days" },
                { code: "30d",       label: "30 days" },
                { code: "1y",        label: "1 year" },
                { code: "permanent", label: "Permanent" },
              ].map((p) => {
                const active = banForm.preset === p.code;
                return (
                  <Button
                    key={p.code}
                    type="button"
                    size="sm"
                    variant={active ? "default" : "outline"}
                    onClick={() =>
                      setBanForm({ ...banForm, preset: p.code, duration_seconds: 0 })
                    }
                    data-testid={`firewall-ban-preset-${p.code}`}
                  >
                    {p.label}
                  </Button>
                );
              })}
            </div>
            <Input
              type="number"
              placeholder="custom seconds (overrides preset)"
              value={banForm.duration_seconds || ""}
              onChange={(e) =>
                setBanForm({
                  ...banForm,
                  duration_seconds: Number(e.target.value) || 0,
                  preset: e.target.value ? "" : banForm.preset,
                })
              }
              data-testid="firewall-ban-duration"
              className="mt-2"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              {t(
                "firewall.banDurationHint",
                "Pick a preset or enter custom seconds. \"Permanent\" stamps the ban with a year-9999 expiry so the engine silent-drops every packet from that IP forever — perfect for persistent scanners that keep retrying."
              )}
            </p>
          </div>
        </div>
      </InlineEditorShell>
    );
  }

  return (
    <div data-testid={scope === "platform" ? "page-platform-firewall" : "page-firewall"}>
      <PageHeader
        title={t("firewall.title")}
        subtitle={
          scope === "platform" ? t("firewall.subtitlePlatform") : t("firewall.subtitleTenant")
        }
        icon={ShieldX}
        actions={
          <Button onClick={() => setBanOpen(true)} data-testid="firewall-ban-add">
            <Plus className="h-4 w-4 mr-1" /> {t("firewall.addBan")}
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPICard
          label={t("firewall.kpi.activeBans")}
          value={activeBans.length}
          icon={Ban}
          testid="kpi-fw-active"
        />
        <KPICard
          label={t("firewall.kpi.autoBans")}
          value={autoBans.length}
          icon={ShieldX}
          testid="kpi-fw-auto"
        />
        <KPICard
          label={t("firewall.kpi.rules")}
          value={rules.filter((r) => r.enabled).length}
          icon={Clock}
          testid="kpi-fw-rules"
        />
        <KPICard
          label={t("firewall.kpi.history")}
          value={bans.length}
          icon={Clock}
          testid="kpi-fw-history"
        />
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm font-medium">{t("firewall.banList")}</div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1 text-[11px] text-[hsl(var(--danger))] border-[hsl(var(--danger))]/40 hover:bg-[hsl(var(--danger))]/10"
              onClick={clearAllBans}
              disabled={activeBans.length === 0}
              data-testid="firewall-bans-clear-all"
              title={t(
                "firewall.clearAllBansTitle",
                "Delete every active ban row in this scope.",
              )}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t("firewall.clearAllBans", "Clear all bans")}
            </Button>
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
              <Switch
                checked={includeExpired}
                onCheckedChange={setIncludeExpired}
                data-testid="firewall-include-expired"
              />
              {t("firewall.includeExpired")}
            </label>
          </div>
        </CardContent>
      </Card>

      {offenders.length > 0 && (
        <Card className="mb-4 border-[hsl(var(--danger))]/40"
              data-testid="firewall-offenders-card">
          <CardContent className="p-4">
            <div className="text-sm font-medium mb-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--danger))]" />
              {t("firewall.repeatOffenders", "Repeat offenders (last 24h)")}
              <StatusPill kind="danger">{offenders.length}</StatusPill>
              {scope === "platform" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto h-7 gap-1 text-[11px]"
                  title={t(
                    "firewall.clearAllOffendersTitle",
                    "Clear every engine-applied ban in the last 24h",
                  )}
                  onClick={handleClearAllOffenders}
                  data-testid="firewall-offenders-clear-all"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t("firewall.clearAllOffenders", "Clear all")}
                </Button>
              )}
            </div>
            <div className="max-h-48 overflow-y-auto">
              <Table>
                <TableHeader className="sticky-table-head">
                  <TableRow>
                    <TableHead>{t("firewall.cols.ip")}</TableHead>
                    <TableHead>{t("firewall.repeatCount", "Bans")}</TableHead>
                    <TableHead>{t("firewall.lastBan", "Last ban")}</TableHead>
                    <TableHead>{t("firewall.topReason", "Reason")}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {offenders.map((o) => (
                    <TableRow key={o.ip} data-testid={`offender-row-${o.ip}`}>
                      <TableCell className="font-mono">{o.ip}</TableCell>
                      <TableCell>
                        <StatusPill kind="danger">{o.count}×</StatusPill>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {o.last_ban_at
                          ? new Date(o.last_ban_at).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {(o.reasons || []).slice(0, 1).join(", ") || "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {[
                            { code: "1d",        label: "1d" },
                            { code: "30d",       label: "30d" },
                            { code: "1y",        label: "1y" },
                            { code: "permanent", label: "∞" },
                          ].map((p) => (
                            <Button
                              key={p.code}
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-[11px]"
                              title={`Ban ${o.ip} for ${p.label}`}
                              onClick={async () => {
                                try {
                                  await api.post(`${base}/bans`, {
                                    ip: o.ip,
                                    reason: `repeat offender: ${o.count}× bans / 24h`,
                                    preset: p.code,
                                  });
                                  toast.success(`Banned ${o.ip} (${p.label})`);
                                  load();
                                } catch (e) {
                                  toast.error(
                                    e?.response?.data?.detail || "Ban failed"
                                  );
                                }
                              }}
                              data-testid={`offender-ban-${o.ip}-${p.code}`}
                            >
                              {p.label}
                            </Button>
                          ))}
                          {scope === "platform" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/10 hover:text-[hsl(var(--danger))]"
                              title={t(
                                "firewall.clearOffender",
                                "Clear bans for this IP",
                              )}
                              onClick={() => handleDeleteOffender(o.ip)}
                              data-testid={`offender-clear-${o.ip}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {activeBans.length === 0 && !includeExpired ? (
        <EmptyState
          title={t("firewall.empty")}
          description={t("firewall.emptyDesc")}
        />
      ) : (
        <Card className="mb-6">
          <CardContent className="p-0">
            <Table>
              <TableHeader className="sticky-table-head">
                <TableRow>
                  <TableHead data-testid="firewall-col-ip">{t("firewall.cols.ip")}</TableHead>
                  {scope === "platform" && (
                    <TableHead data-testid="firewall-col-org">
                      {t("firewall.cols.org")}
                    </TableHead>
                  )}
                  <TableHead data-testid="firewall-col-reason">
                    {t("firewall.cols.reason")}
                  </TableHead>
                  <TableHead data-testid="firewall-col-bannedat">
                    {t("firewall.cols.bannedAt")}
                  </TableHead>
                  <TableHead data-testid="firewall-col-expires">
                    {t("firewall.cols.expires")}
                  </TableHead>
                  <TableHead data-testid="firewall-col-actor">
                    {t("firewall.cols.actor")}
                  </TableHead>
                  <TableHead className="text-right">{t("common.actions", "Actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bans.map((b) => {
                  const expired = new Date(b.expires_at).getTime() <= now;
                  const isPermanent =
                    !!b.permanent ||
                    (b.expires_at || "").startsWith("9999-");
                  return (
                    <TableRow
                      key={b.id}
                      className="row-hover"
                      data-testid={`firewall-row-${b.id}`}
                    >
                      <TableCell className="font-mono text-xs">{b.ip}</TableCell>
                      {scope === "platform" && (
                        <TableCell className="font-mono text-[11px] text-muted-foreground">
                          {b.org_id || "global"}
                        </TableCell>
                      )}
                      <TableCell className="text-xs">
                        {b.reason}
                        {b.escalation_tier > 0 && (
                          <span className="ml-1 inline-block rounded bg-[hsl(var(--warn))]/15 px-1 py-0.5 text-[9px] uppercase tracking-wider text-[hsl(var(--warn))]">
                            tier {b.escalation_tier}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-[11px] text-muted-foreground">
                        {new Date(b.banned_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {isPermanent ? (
                          <StatusPill kind="danger">permanent ∞</StatusPill>
                        ) : expired ? (
                          <StatusPill kind="muted">expired</StatusPill>
                        ) : (
                          <StatusPill kind="warn">
                            {new Date(b.expires_at).toLocaleString()}
                          </StatusPill>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusPill kind={b.actor_scope === "engine" ? "info" : "ok"}>
                          {b.actor_scope}
                        </StatusPill>
                        <div className="text-[10px] font-mono text-muted-foreground">
                          {b.actor}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1 px-2 text-[11px] text-[hsl(var(--ok))] hover:bg-[hsl(var(--ok))]/10 hover:text-[hsl(var(--ok))]"
                            onClick={() => unbanAndTrust(b)}
                            title={t(
                              "firewall.trustForeverTitle",
                              "Unban + trust forever (add to allowlist)",
                            )}
                            data-testid={`firewall-trust-${b.id}`}
                          >
                            <ShieldCheck className="h-4 w-4" />
                            <span className="hidden md:inline">
                              {t("firewall.trustForeverShort", "Trust")}
                            </span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-[hsl(var(--danger))]"
                            onClick={() => unban(b)}
                            title={t("common.delete", "Delete")}
                            data-testid={`firewall-unban-${b.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-medium flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[hsl(var(--ok))]" />
                {t("firewall.allowlistTitle", "Firewall allowlist")}
                <StatusPill kind="ok">{allowlist.length}</StatusPill>
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {t(
                  "firewall.allowlistDesc",
                  "IPs and CIDRs in this list are never banned by the engine, even if they trip per-event-type or cumulative rules. Loopback (127.0.0.1, ::1) is always trusted automatically.",
                )}
              </div>
            </div>
          </div>
          <div
            className="flex flex-wrap items-end gap-2 mb-3"
            data-testid="firewall-allowlist-form"
          >
            <div className="flex-1 min-w-[140px]">
              <Label className="text-[11px]">{t("firewall.allowlistIp", "IP or CIDR")}</Label>
              <Input
                value={allowForm.cidr}
                onChange={(e) => setAllowForm({ ...allowForm, cidr: e.target.value })}
                placeholder="203.0.113.42 or 198.51.100.0/24"
                data-testid="firewall-allowlist-cidr"
              />
            </div>
            <div className="flex-[2] min-w-[160px]">
              <Label className="text-[11px]">{t("firewall.allowlistLabel", "Label")}</Label>
              <Input
                value={allowForm.label}
                onChange={(e) => setAllowForm({ ...allowForm, label: e.target.value })}
                placeholder={t(
                  "firewall.allowlistLabelHint",
                  "e.g. Office WAN, Zoiper user John, etc.",
                )}
                data-testid="firewall-allowlist-label"
              />
            </div>
            <Button
              onClick={addAllowlistEntry}
              disabled={allowAdding || !allowForm.cidr.trim()}
              data-testid="firewall-allowlist-add"
            >
              <Plus className="h-4 w-4 mr-1" />
              {t("firewall.allowlistAdd", "Add")}
            </Button>
          </div>
          {allowlist.length === 0 ? (
            <div className="text-[11px] text-muted-foreground italic py-2">
              {t("firewall.allowlistEmpty", "No custom allowlist entries. Loopback is always trusted.")}
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky-table-head">
                <TableRow>
                  <TableHead>{t("firewall.allowlistIp", "IP or CIDR")}</TableHead>
                  <TableHead>{t("firewall.allowlistLabel", "Label")}</TableHead>
                  <TableHead>{t("firewall.allowlistScope", "Scope")}</TableHead>
                  <TableHead>{t("firewall.allowlistCreated", "Added")}</TableHead>
                  <TableHead className="text-right">{t("common.actions", "Actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allowlist.map((row) => (
                  <TableRow key={row.id} data-testid={`firewall-allowlist-row-${row.id}`}>
                    <TableCell className="font-mono text-xs">{row.cidr}</TableCell>
                    <TableCell className="text-xs">{row.label || "—"}</TableCell>
                    <TableCell>
                      <StatusPill kind={row.scope === "platform" ? "security" : "info"}>
                        {row.scope === "platform"
                          ? t("firewall.allowlistScopePlatform", "global")
                          : t("firewall.allowlistScopeTenant", "tenant")}
                      </StatusPill>
                    </TableCell>
                    <TableCell className="text-[11px] text-muted-foreground">
                      <div>{row.created_at ? new Date(row.created_at).toLocaleString() : "—"}</div>
                      {row.created_by && (
                        <div className="font-mono text-[10px]">{row.created_by}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-[hsl(var(--danger))]"
                        onClick={() => removeAllowlistEntry(row)}
                        data-testid={`firewall-allowlist-remove-${row.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-medium">{t("firewall.rulesTitle")}</div>
              <div className="text-[11px] text-muted-foreground">
                {t("firewall.rulesDesc")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={resetToDefaults}
                disabled={savingRules}
                data-testid="firewall-reset-defaults"
                title={t("firewall.resetTitle", "Reset every rule to the engine baseline (recommended secure defaults).")}
              >
                {t("firewall.resetDefaults", "Reset to defaults")}
              </Button>
              <Button onClick={saveRules} disabled={savingRules} data-testid="firewall-save-rules">
                <Save className="h-4 w-4 mr-1" />{" "}
                {savingRules ? t("common.loading") : t("common.save")}
              </Button>
            </div>
          </div>
          {/* Cumulative-IP info card. This is enforced by the engine but
              not editable per-rule (it spans all event types), so we
              surface it as a read-only callout above the editable rows. */}
          <div
            className="mb-4 rounded-md border border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/5 px-3 py-2 text-[11px] flex flex-wrap items-center gap-2"
            data-testid="firewall-cumulative-info"
          >
            <span className="font-medium text-[hsl(var(--info))]">
              {t("firewall.cumulativeTitle", "Cumulative-IP safety net")}
            </span>
            <span className="text-muted-foreground">
              {t(
                "firewall.cumulativeDesc",
                "Any IP firing 15 security events of any autoban-eligible type within 5 minutes earns a 1h ban regardless of per-type breakdown — catches slow-and-low scanners. Configurable via engine env vars."
              )}
            </span>
          </div>
          <Table>
            <TableHeader className="sticky-table-head">
              <TableRow>
                <TableHead>{t("firewall.cols.eventType")}</TableHead>
                <TableHead className="text-right">{t("firewall.cols.window")}</TableHead>
                <TableHead className="text-right">{t("firewall.cols.maxCount")}</TableHead>
                <TableHead className="text-right">{t("firewall.cols.banDuration")}</TableHead>
                <TableHead>{t("firewall.cols.enabled")}</TableHead>
                <TableHead>{t("firewall.cols.source")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((r) => (
                <TableRow key={r.event_type} data-testid={`firewall-rule-${r.event_type}`}>
                  <TableCell className="font-mono text-xs">{r.event_type}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      min="1"
                      value={r.window_seconds}
                      onChange={(e) => setRule(r.event_type, { window_seconds: e.target.value })}
                      className="w-24 ml-auto"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      min="1"
                      value={r.max_count}
                      onChange={(e) => setRule(r.event_type, { max_count: e.target.value })}
                      className="w-24 ml-auto"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      min="0"
                      value={r.ban_duration_seconds}
                      onChange={(e) => setRule(r.event_type, { ban_duration_seconds: e.target.value })}
                      className="w-28 ml-auto"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!!r.enabled}
                      onCheckedChange={(v) => setRule(r.event_type, { enabled: v })}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusPill
                      kind={r.source === "org" ? "info" : r.source === "global" ? "security" : "muted"}
                    >
                      {r.source}
                    </StatusPill>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
